import React, { useEffect, useMemo, useRef, useState } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Icon from "react-feather";
import HorizontalSidebar from "./horizontalSidebar";
import CollapsedSidebar from "./collapsedSidebar";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import SidebarData from "../../core/json/siderbar_data";
import { _api } from "../../api/Api";
import { useDispatch, useSelector } from "react-redux";
import { RESET_STORE, activeUser, userPermissions } from "../../core/redux/action";

// Distributor login = no dealer (empty or 0) — only they get pending counters
const isDistributorUser = () => !(Number(JSON.parse(localStorage.getItem("permissions") || "{}")?.dealerId) > 0);

// Top menus listed under "Administration"; everything else is a department ("Core Departments")
const ADMIN_MENUS = ["Finance", "Reporting", "Reports", "HR & Payroll", "Setup (OEM)", "Setup", "Discount"];

// Icon tile colours, picked by the menu's name so a menu keeps its colour
const TINTS = [
  { c: "#2563eb", bg: "#eff6ff" }, { c: "#0d9488", bg: "#f0fdfa" }, { c: "#16a34a", bg: "#f0fdf4" },
  { c: "#d97706", bg: "#fffbeb" }, { c: "#7c3aed", bg: "#f5f3ff" }, { c: "#db2777", bg: "#fdf2f8" },
  { c: "#0891b2", bg: "#ecfeff" }, { c: "#ea580c", bg: "#fff7ed" },
];
const tintOf = (label = "") => TINTS[[...label].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % TINTS.length];

const Sidebar = () => {
  const Location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state.user);
  const loginName = loginUser?.fullName || "";
  const loginRole = loginUser?.roles?.[0]?.roleName || "";

  // Distributor: Approvals open by default (after refresh too)
  const [subOpen, setSubopen] = useState(isDistributorUser() ? "Approvals" : "");
  const [subsidebar, setSubsidebar] = useState("");
  const [subSubSidebar, setSubSubSidebar] = useState(""); // ✅ 4th level

  // Pending records per menu link (distributor only) — refreshed on every page change and every 2 min
  const [menuCounts, setMenuCounts] = useState({});
  useEffect(() => {
    if (!isDistributorUser()) return;
    const load = () => _api("GET", "MenuCounts", null, (res) => { if (res?.isSuccess) setMenuCounts(res.data || {}); });
    load();
    const timer = setInterval(load, 120000);
    return () => clearInterval(timer);
  }, [Location.pathname]);

  // Open the menus that hold the current page (on refresh and on every navigation)
  useEffect(() => {
    const path = Location.pathname;
    const has = (node) => node?.link === path || (node?.submenuItems || []).some(has);
    SidebarData.forEach((main) => (main?.submenuItems || []).forEach((title) => {
      if (!title?.submenu || !has(title)) return;
      setSubopen(title.label);
      (title.submenuItems || []).forEach((item) => {
        if (!item?.submenu || !has(item)) return;
        setSubsidebar(item.label);
        (item.submenuItems || []).forEach((deep) => {
          if (deep?.submenu && has(deep)) setSubSubSidebar(deep.label);
        });
      });
    }));
  }, [Location.pathname]);

  // A link's own count, or the total of everything under a parent menu
  const countOf = (node) => node?.submenu
    ? (node.submenuItems || []).reduce((sum, child) => sum + countOf(child), 0)
    : (menuCounts[node?.link] || 0);
  // An open parent menu shows the counts on its items, so its own total is hidden
  const CountPill = ({ node, open }) => {
    const n = countOf(node);
    return n > 0 && !open ? <span className="menu-count">{n > 99 ? "99+" : n}</span> : null;
  };

  const toggleSidebar = (title) => {
    setSubopen(prev => prev === title ? "" : title);
  };

  const toggleSubsidebar = (subitem) => {
    setSubsidebar(prev => prev === subitem ? "" : subitem);
  };

  const toggleSubSubSidebar = (label) => {
    setSubSubSidebar(prev => prev === label ? "" : label);
  };

  // ── Jump to menu: every page of the menu, searchable by its name or its path (Ctrl+K) ──
  const [query, setQuery] = useState("");
  const [hit, setHit] = useState(0);
  const searchRef = useRef(null);
  const pages = useMemo(() => {
    const out = [];
    const walk = (node, trail) => {
      if (!node) return;
      const label = (node.label || "").trim();
      if (node.link && !node.submenu) out.push({ label, link: node.link, trail: trail.join(" › ") });
      (node.submenuItems || []).forEach((child) => walk(child, [...trail, label]));
    };
    SidebarData.forEach((main) => (main?.submenuItems || []).forEach((title) => walk(title, [])));
    return out;
  }, []);
  const q = query.trim().toLowerCase();
  const results = q ? pages.filter((p) => `${p.label} ${p.trail}`.toLowerCase().includes(q)).slice(0, 10) : [];
  const go = (page) => { if (!page) return; setQuery(""); navigate(page.link); };
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHit((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHit((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") go(results[hit]);
    else if (e.key === "Escape") { setQuery(""); e.currentTarget.blur(); }
  };

  const handleLogout = () => {
    localStorage.removeItem("permissions");
    localStorage.removeItem("permissionsMap");
    dispatch(activeUser({}));
    dispatch(userPermissions({}));
    dispatch({ type: RESET_STORE });
    navigate("/sign-in");
  };

  // The top menus split into two sections
  const menus = SidebarData.flatMap((main) => main?.submenuItems || []);
  const sections = [
    { title: "Core Departments", items: menus.filter((m) => !ADMIN_MENUS.includes(m?.label)) },
    { title: "Administration", items: menus.filter((m) => ADMIN_MENUS.includes(m?.label)) },
  ].filter((s) => s.items.length);

  const renderMenu = (title, i) => {
    // link_array for active state detection
    let link_array = [];
    title?.submenuItems?.map((link) => {
      link_array?.push(link?.link);
      if (link?.submenu) {
        link?.submenuItems?.map((item) => {
          link_array?.push(item?.link);
          if (item?.submenu) {
            item?.submenuItems?.map((deepItem) => {
              link_array?.push(deepItem?.link); // ✅ 4th level links
            });
          }
        });
      }
      return link_array;
    });
    title.links = link_array;
    const tint = tintOf(title?.label);

    return (
      <li className="submenu" key={i}>

        {/* ===== LEVEL 1 ===== */}
        <Link
          to={title?.link}
          onClick={(e) => {
            if (title?.submenu) {
              e.preventDefault();
              toggleSidebar(title?.label);
            }
          }}
          className={`${subOpen == title?.label ? "subdrop" : ""} ${
            Location.pathname === title?.link ||
            Location.pathname.startsWith(title?.link + "/") ||
            title?.links?.includes(Location.pathname)
              ? "active" : ""
          }`}
          title={title?.label}
        >
          <span className="menu-icon" style={{ "--tint": tint.c, "--tint-bg": tint.bg }}>{title?.icon}</span>
          <span className="menu-label">{title?.label}</span>
          <CountPill node={title} open={subOpen == title?.label} />
          <span className={title?.submenu ? "menu-arrow" : ""} />
        </Link>

        {/* ===== LEVEL 2 ===== */}
        <ul style={{ display: subOpen == title?.label ? "block" : "none" }}>
          {title?.submenuItems?.map((item, titleIndex) => (
            <li className="submenu submenu-two" key={titleIndex}>
              <Link
                to={item?.link}
                className={`${
                  item?.submenuItems?.map(l => l?.link).includes(Location.pathname) ||
                  item?.link == Location.pathname ? "active" : ""
                } ${subsidebar == item?.label ? "subdrop" : ""}`}
                onClick={() => {
                  if (item?.submenu) toggleSubsidebar(item?.label);
                }}
              >
                <span className="menu-label">{item?.label}</span>
                <CountPill node={item} open={subsidebar == item?.label} />
                <span className={item?.submenu ? "menu-arrow inside-submenu" : ""} />
              </Link>

              {/* ===== LEVEL 3 ===== */}
              <ul style={{ display: subsidebar == item?.label ? "block" : "none" }}>
                {item?.submenuItems?.map((items, idx) => (
                  <li className="submenu submenu-three" key={idx}>
                    <Link
                      to={items?.link}
                      className={`${
                        items?.submenuItems?.map(l => l?.link).includes(Location.pathname) ||
                        items?.link == Location.pathname ? "active" : ""
                      } ${subSubSidebar == items?.label ? "subdrop" : ""}`}
                      onClick={() => {
                        if (items?.submenu) toggleSubSubSidebar(items?.label);
                      }}
                      title={items?.label}
                    >
                      <span className="menu-label">{items?.label}</span>
                      <CountPill node={items} open={subSubSidebar == items?.label} />
                      <span className={items?.submenu ? "menu-arrow inside-submenu" : ""} />
                    </Link>

                    {/* ===== LEVEL 4 ===== */}
                    {items?.submenuItems?.length > 0 && (
                      <ul style={{ display: subSubSidebar == items?.label ? "block" : "none" }}>
                        {items?.submenuItems?.map((deepItem, deepIndex) => (
                          <li className="submenu-four" key={deepIndex}>
                            <Link
                              to={deepItem?.link}
                              className={
                                deepItem?.link == Location.pathname ? "active" : ""
                              }
                              title={deepItem?.label}
                            >
                              <span className="menu-label">{deepItem?.label}</span>
                              <CountPill node={deepItem} open={false} />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}

                  </li>
                ))}
              </ul>

            </li>
          ))}
        </ul>

      </li>
    );
  };

  return (
    <div>
      <div className="sidebar sidebar-modern" id="sidebar">
        {/* Jump to menu */}
        <div className="sidebar-search">
          <Icon.Search size={14} className="sidebar-search-icon" />
          <input ref={searchRef} type="text" placeholder="Jump to menu..." value={query}
            onChange={(e) => { setQuery(e.target.value); setHit(0); }} onKeyDown={onSearchKey} />
          {query ? (
            <button type="button" className="sidebar-search-clear" onClick={() => setQuery("")} title="Clear"><Icon.X size={12} /></button>
          ) : <kbd>Ctrl K</kbd>}
        </div>

        <Scrollbars style={{ flex: 1, height: "auto", minHeight: 0 }}>
          <div className="sidebar-inner slimscroll">
            {q ? (
              <ul className="sidebar-search-results">
                {results.length ? results.map((p, i) => (
                  <li key={p.link}>
                    <button type="button" className={i === hit ? "hit" : ""} onMouseEnter={() => setHit(i)} onClick={() => go(p)}>
                      <span className="r-label">{p.label}</span>
                      {p.trail && <span className="r-trail">{p.trail}</span>}
                    </button>
                  </li>
                )) : <li className="sidebar-search-empty">No menu matches &quot;{query}&quot;</li>}
              </ul>
            ) : (
              <div id="sidebar-menu" className="sidebar-menu">
                <ul>
                  {sections.map((section) => (
                    <li className="submenu-open" key={section.title}>
                      <div className="sb-section">{section.title}</div>
                      <ul>
                        {section.items.map((title, i) => renderMenu(title, `${section.title}-${i}`))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Scrollbars>
        {/* Logged-in user, fixed at the bottom of the sidebar */}
        {loginName && (
          <div className="sidebar-user" title={`${loginName}${loginRole ? ` – ${loginRole}` : ""}`}>
            <span className="sidebar-user-avatar">
              {loginName.trim().charAt(0).toUpperCase()}
              <span className="sidebar-user-online" />
            </span>
            <span className="sidebar-user-text">
              <span className="sidebar-user-name">
                {loginName}
                {isDistributorUser() && <span className="sidebar-user-chip">HQ</span>}
              </span>
              {loginRole && <span className="sidebar-user-role">{loginRole}</span>}
            </span>
            <button type="button" className="sidebar-user-btn" title="Logout" onClick={handleLogout}>
              <Icon.LogOut size={15} />
            </button>
          </div>
        )}
        <div className="powered-by d-none">
          <ImageWithBasePath src="/assets/img/DeepDMS.png" alt="img" />
          <h6 className="submenu-hdr text-muted">Powered By DTS Autosoft</h6>
        </div>
      </div>
      <HorizontalSidebar />
      <CollapsedSidebar />
    </div>
  );
};

export default Sidebar;
